// A group's chat: messages as bubbles, sending text and photos, spots on the map, leaving the group.
import type { GroupMessage, PinType } from "../../shared/model.ts";
import { dbPut } from "../db.ts";
import { $, el, input, toast } from "../dom.ts";
import { compass, dFmt, dFmtY, fmtNum, fmtWhen, pinTypeLabel, t, tFmt } from "../i18n.ts";
import { bigMap } from "../map/bigmap.ts";
import { addPin, isPinType } from "../map/places.ts";
import { blobToDataUrl, shrink } from "../photos.ts";
import { closeSheet, sheet } from "../sheet.ts";
import { errorMessage, uid } from "../util.ts";
import { setView } from "../views.ts";
import { clubEventText, foldClub, isClubType, openClub } from "./club.ts";
import { foldSocial, refreshPost, setChatTab, socialFooter, chatTab, renderFeed } from "./feed.ts";
import { groupMessages, groups, leaveGroup, me, saveGroup, send, syncGroup, updateBadge } from "./store.ts";
import { openInvite, renderGroups } from "./ui.ts";

export const chatDialog = $<HTMLDialogElement>("#chat");
/** The group whose chat is open, if any. */
export let openChatId: string | null = null;
let chatTimer = 0;
export const chatGroup = () => groups.find((g) => g.id === openChatId);

export async function openChat(id: string): Promise<void> {
  const g = groups.find((x) => x.id === id);
  if (!g) return;
  openChatId = id;
  g.unread = 0;
  await saveGroup(g);
  updateBadge(); renderGroups();
  $("#chat-title").textContent = g.name;
  input("#chat-text").value = "";
  disarmLeave();
  if (sheet.open) closeSheet();
  if (!chatDialog.open) chatDialog.showModal();
  setChatTab("chat");
  await renderChat(true);
  void syncGroup(g).then((n) => { if (n) void renderChat(true); });
  // new messages every 5 s while the chat is open
  clearInterval(chatTimer);
  chatTimer = window.setInterval(async () => {
    if (document.visibilityState === "visible" && openChatId === id && (await syncGroup(g))) void renderChat(true);
  }, 5000);
}
function closeChat(): void {
  clearInterval(chatTimer);
  openChatId = null;
  if (chatDialog.open) chatDialog.close();
  renderGroups();
}
$("#chat-close").addEventListener("click", closeChat);
chatDialog.addEventListener("close", () => { clearInterval(chatTimer); openChatId = null; });
$("#chat-invite").addEventListener("click", () => { const g = chatGroup(); if (g) openInvite(g); });
$("#chat-club").addEventListener("click", () => { const g = chatGroup(); if (g) void openClub(g.id); });

/* ---------- bubbles ---------- */
function bubbleBody(m: GroupMessage): (HTMLElement | null)[] {
  if (m.type === "text") return [el("p", { class: "btext", text: m.text })];
  if (m.type === "photo") return [el("img", { class: "bimg", src: m.image, alt: t("msgPhoto") }), m.text ? el("p", { class: "btext", text: m.text }) : null];
  if (m.type === "catch") {
    const c = m.catch;
    const size = [c.weightKg != null ? fmtNum(c.weightKg, 2) + " kg" : "", c.lengthCm != null ? fmtNum(c.lengthCm) + " cm" : "", c.released ? t("released") : ""].filter(Boolean).join(" · ");
    const wx = [c.temp != null ? Math.round(c.temp) + "°C" : "", c.wind != null ? fmtNum(c.wind, 0) + " m/s " + (c.windDir != null ? compass(c.windDir) : "") : "",
      c.water != null ? t("waterChip", fmtNum(c.water, 0)) : ""].filter(Boolean).join(" · ");
    return [m.image ? el("img", { class: "bimg", src: m.image, alt: c.species || "" }) : null,
      el("div", { class: "bcatch" }, el("b", { text: c.species || t(c.kind || "fish") }),
        size ? el("span", { class: "num", text: size }) : null,
        el("small", { class: "num", text: [c.time ? fmtWhen(c.time) : "", c.place].filter(Boolean).join(" · ") }),
        wx ? el("small", { class: "num", text: wx }) : null,
        c.loc ? spotButtons(c.loc.lat, c.loc.lon, c.species || t("fish"), c.kind === "game" ? "stand" : "spot") : el("small", { class: "muted", text: t("msgSpotHidden") }))];
  }
  if (m.type === "pin") {
    const p = m.pin;
    return [el("div", { class: "bcatch" }, el("b", { text: p.name || pinTypeLabel(p.type) }), el("small", { text: pinTypeLabel(p.type) }),
      p.notes ? el("p", { class: "btext", text: p.notes }) : null, spotButtons(p.lat, p.lon, p.name || pinTypeLabel(p.type), p.type))];
  }
  return [];
}

/** "Show on map" and "Save as pin" for a shared spot. */
export function spotButtons(lat: number, lon: number, name: string, type: string): HTMLElement {
  const pinType: PinType = isPinType(type) ? type : "other";
  return el("div", { class: "bbtns" },
    el("button", { class: "btn small", type: "button", onclick: () => showSpot(lat, lon) }, t("msgShowMap")),
    el("button", { class: "btn small", type: "button", onclick: async (e) => {
      const btn = e.currentTarget;
      const p = { id: uid(), type: pinType, name, notes: "", lat, lon, createdAt: Date.now(), source: "group" as const };
      try { await dbPut("pins", p); addPin(p); btn.disabled = true; btn.textContent = t("msgSaved"); }
      catch { toast(t("saveFailed")); }
    } }, t("msgSavePin")));
}
let spotMarker: L.CircleMarker | null = null;
function showSpot(lat: number, lon: number): void {
  closeChat();
  setView("map");
  requestAnimationFrame(() => {
    if (!bigMap) return;
    bigMap.setView([lat, lon], 15);
    spotMarker?.remove();
    spotMarker = L.circleMarker([lat, lon], { radius: 14, className: "spot-ring" }).addTo(bigMap);
  });
}

export async function renderChat(scroll: boolean): Promise<void> {
  const g = chatGroup();
  if (!g) return;
  const msgs = await groupMessages(g.id), box = $("#chat-list");
  const atBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 80;
  $("#chat-sub").textContent = t("groupMembersSeen", new Set(msgs.map((m) => m.from)).size);
  const social = foldSocial(msgs);
  const stands = foldClub(msgs).stands;
  const out: HTMLElement[] = [];
  let day = "";
  for (const m of msgs) {
    const d = dFmtY.format(m.ts);
    if (d !== day) { day = d; out.push(el("div", { class: "cday", text: dFmt.format(m.ts) })); }
    if (m.type === "join") { out.push(el("div", { class: "cevent", text: t("msgJoined", m.name) })); continue; }
    if (m.type === "like" || m.type === "comment") continue; // shown with the post (photo feed)
    if (isClubType(m.type)) { const txt = clubEventText(m, stands); if (txt) out.push(el("div", { class: "cevent club", text: txt })); continue; }
    const mine = m.from === me.id;
    out.push(el("div", { class: "bubble" + (mine ? " mine" : "") },
      mine ? null : el("span", { class: "bname", text: m.name }),
      ...bubbleBody(m),
      socialFooter(m, social),
      el("span", { class: "btime num", text: tFmt.format(m.ts) })));
  }
  if (!msgs.some((m) => !["join", "clubstate", "like", "comment"].includes(m.type))) out.push(el("div", { class: "cevent", text: t("chatEmpty") }));
  box.replaceChildren(...out);
  if (scroll || atBottom) box.scrollTop = box.scrollHeight;
  if (chatTab === "feed") void renderFeed();
  void refreshPost();
}

/* ---------- writing ---------- */
async function sendFromComposer(payload: Parameters<typeof send>[1]): Promise<boolean> {
  const g = chatGroup();
  if (!g) return false;
  const btn = $<HTMLButtonElement>("#chat-send");
  btn.disabled = true;
  try { await send(g, payload); await renderChat(true); renderGroups(); return true; }
  catch (e) { console.error(e); toast(errorMessage(e).includes("too big") ? t("msgTooBig") : t("msgNotSent")); return false; }
  finally { btn.disabled = false; }
}
$("#chat-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const text = input("#chat-text").value.trim();
  if (!text) return;
  if (await sendFromComposer({ type: "text", text })) input("#chat-text").value = "";
  input("#chat-text").focus();
});
$("#chat-text").addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); $<HTMLFormElement>("#chat-form").requestSubmit(); } });
$("#chat-photo").addEventListener("change", async (e) => {
  const fileInput = e.currentTarget as HTMLInputElement, f = fileInput.files?.[0];
  fileInput.value = "";
  if (!f) return;
  try {
    const image = await blobToDataUrl(await shrink(f, 1280, 0.78));
    if (await sendFromComposer({ type: "photo", image, text: input("#chat-text").value.trim() })) input("#chat-text").value = "";
  } catch (err) { console.error(err); toast(t("photoBad")); }
});

/* ---------- leaving (two taps) ---------- */
let leaveArmed = false;
function disarmLeave(): void {
  leaveArmed = false;
  const b = $("#chat-leave");
  b.classList.remove("armed");
  b.textContent = t("groupLeave");
}
$("#chat-leave").addEventListener("click", async () => {
  const b = $("#chat-leave");
  if (!leaveArmed) { leaveArmed = true; b.classList.add("armed"); b.textContent = t("groupLeaveConfirm"); setTimeout(disarmLeave, 3500); return; }
  const g = chatGroup();
  if (!g) return;
  await leaveGroup(g);
  closeChat(); updateBadge(); toast(t("groupLeft"));
});
